import { useCallback, useState } from 'react';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';

// Data structure sent to backend (aligned with FingerprintData in backend)
export interface FingerprintData {
  visitorId: string;
  requestId: string;
  confidence: number;
  countryCode: string;
}

interface UseFingerprintResult {
  getFingerprint: () => Promise<FingerprintData | null>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for collecting fingerprint data during login.
 *
 * Usage:
 * ```tsx
 * const { getFingerprint, isLoading, error } = useFingerprint();
 *
 * const handleLogin = async () => {
 *   const fpData = await getFingerprint(); // null if unavailable
 *   // Include fpData in login request (backend handles null gracefully)
 * };
 * ```
 */
export function useFingerprint(): UseFingerprintResult {
  const [error, setError] = useState<Error | null>(null);

  // Use the Fingerprint SDK hook with extended result (includes geolocation)
  // immediate: false means we manually trigger the data collection
  const { getData, isLoading } = useVisitorData(
    { extendedResult: true },
    { immediate: false }
  );

  const getFingerprint = useCallback(async (): Promise<FingerprintData | null> => {
    try {
      setError(null);

      // Check if Fingerprint is configured (API key provided)
      if (!import.meta.env.VITE_FINGERPRINT_PUBLIC_KEY) {
        console.log('[Fingerprint] No API key configured, skipping');
        return null;
      }

      const result = await getData({ ignoreCache: true });

      if (!result) {
        console.warn('[Fingerprint] No result from SDK');
        return null;
      }

      // Debug: log full result to see what's available
      console.log('[Fingerprint] Full SDK result:', result);
      console.log('[Fingerprint] ipLocation:', result.ipLocation);

      // Extract country code from IP location (extendedResult provides this)
      const countryCode = result.ipLocation?.country?.code ?? 'GLOBAL';

      const fingerprintData: FingerprintData = {
        visitorId: result.visitorId,
        requestId: result.requestId,
        confidence: result.confidence.score,
        countryCode,
      };

      console.log('[Fingerprint] Data collected:', {
        visitorId: fingerprintData.visitorId,
        confidence: fingerprintData.confidence,
        countryCode: fingerprintData.countryCode,
      });

      return fingerprintData;
    } catch (err) {
      // Log but don't throw - fingerprint is optional for graceful degradation
      const error = err instanceof Error ? err : new Error('Fingerprint collection failed');
      console.warn('[Fingerprint] Collection failed:', error.message);
      setError(error);
      return null;
    }
  }, [getData]);

  return {
    getFingerprint,
    isLoading: isLoading ?? false,
    error,
  };
}
